import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { CountryLabel } from "@/components/country-label";
import { aggregatesQuery, countriesFrom, type CountryMetric } from "@/lib/queries";
import { formatCompact, translateCountry } from "@/lib/format";
import { LayoutGrid, List as ListIcon, Search } from "lucide-react";
import { safeLoad } from "@/lib/ssr-safe";
import "@/i18n";

export const Route = createFileRoute("/countries")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Countries · Youtubers Map" },
      { property: "og:title", content: "Countries · Youtubers Map" },
      { property: "og:description", content: "All countries covered by our travel youtubers." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "description", content: "All countries covered by our travel youtubers." },
    ],
  }),
  loader: async ({ context }) => {
    await safeLoad("countries:aggregates", context.queryClient.ensureQueryData(aggregatesQuery));
  },
  component: CountriesPage,
});

function CountriesPage() {
  return (
    <div className="min-h-screen">
      <SiteNav />
      <Suspense fallback={<div className="p-24 text-center text-ink/40">…</div>}>
        <CountriesContent />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function CountriesContent() {
  const { t, i18n } = useTranslation();
  usePageMeta(t("meta.countriesTitle"), t("meta.countriesDesc"));
  const { data: agg } = useSuspenseQuery(aggregatesQuery);
  const [mode, setMode] = useState<CountryMetric>("videos");
  const [viewMode, setViewMode] = useState<"cards" | "list">("list");
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countriesFrom(agg)
      .map((r) => ({ ...r, displayName: translateCountry(r.code, r.name, i18n.language) }))
      .filter((r) => !q || r.displayName.toLowerCase().includes(q) || r.code.toLowerCase().includes(q))
      .sort((a, b) => b[mode] - a[mode]);
  }, [agg, mode, query, i18n.language]);

  const max = rows[0]?.[mode] || 1;

  const heatColor = (n: number) => {
    const t = Math.pow(n / max, 0.6);
    // amber gradient — keep lightness high enough for dark ink text to remain legible
    const l = 96 - t * 26;
    return `oklch(${l}% ${0.02 + t * 0.1} 55)`;
  };

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-4">
        <p className="text-mono-caps text-amber mb-2">{t("countries.kicker")}</p>
        <h1 className="text-3xl md:text-4xl mb-2">{t("countries.title")}</h1>
        <p className="text-ink/60 max-w-2xl">{t("countries.subtitle")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("countries.searchPlaceholder")}
            className="w-full bg-paper ring-hairline text-sm pl-8 pr-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber"
          />
        </div>
        <div className="flex items-center ring-hairline bg-paper text-xs font-mono uppercase tracking-wider">
          {(["videos", "views", "channels"] as CountryMetric[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 transition-colors ${
                mode === m ? "bg-ink text-paper" : "text-ink/60 hover:bg-secondary"
              }`}
            >
              {m === "videos"
                ? t("home.modeVideos")
                : m === "views"
                ? t("home.modeViews")
                : t("home.modeChannels")}
            </button>
          ))}
        </div>
        <div className="flex items-center ring-hairline bg-paper">
          <button
            onClick={() => setViewMode("cards")}
            className={`p-1.5 ${viewMode === "cards" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
            aria-label={t("common.viewCards")}
            title={t("common.viewCards")}
          >
            <LayoutGrid className="size-3.5" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-1.5 ${viewMode === "list" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
            aria-label={t("common.viewList")}
            title={t("common.viewList")}
          >
            <ListIcon className="size-3.5" />
          </button>
        </div>
        <span className="ml-auto text-mono-caps text-ink/50">{rows.length} {t("countries.title").toLowerCase()}</span>
      </section>

      {viewMode === "cards" ? (
        <section className="max-w-7xl mx-auto px-6 pb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
            {rows.map((c) => (
              <Link
                key={c.code}
                to="/country/$code"
                params={{ code: c.code.toLowerCase() }}
                className="ring-hairline p-2.5 hover:ring-amber transition-all group"
                style={{ backgroundColor: heatColor(c[mode]) }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <CountryLabel
                    code={c.code}
                    name={c.displayName}
                    className="font-serif text-sm truncate group-hover:text-amber-deep"
                    size={20}
                  />
                </div>
                <div className="font-mono text-[10px] text-ink/70 tabular-nums">
                  {formatCompact(c[mode])}{" "}
                  {mode === "videos"
                    ? t("countries.videosLabel")
                    : mode === "views"
                    ? t("countries.viewsLabel")
                    : t("countries.channelsLabel")}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <section className="max-w-7xl mx-auto px-6 pb-12">
          <div className="ring-hairline bg-paper overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-ink/10 bg-secondary/40 text-[10px] font-mono uppercase tracking-wider text-ink/50">
                <tr>
                  <th className="text-left px-3 py-2 w-10">#</th>
                  <th className="text-left px-3 py-2">{t("common.country")}</th>
                  <th className="text-right px-3 py-2 w-24">{t("home.modeVideos")}</th>
                  <th className="text-right px-3 py-2 w-28">{t("home.modeViews")}</th>
                  <th className="text-right px-3 py-2 w-24">{t("home.modeChannels")}</th>
                  <th className="px-3 py-2 w-64" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/8">
                {rows.map((c, i) => (
                  <tr key={c.code} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-3 py-1.5 font-mono text-xs text-ink/40">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-3 py-1.5">
                      <Link
                        to="/country/$code"
                        params={{ code: c.code.toLowerCase() }}
                        className="inline-flex items-center gap-1.5 hover:text-amber"
                      >
                        <CountryLabel
                          code={c.code}
                          name={c.displayName}
                          className="font-serif hidden sm:inline-flex"
                          size={20}
                        />
                        <CountryLabel
                          code={c.code}
                          name={c.displayName}
                          showName={false}
                          className="sm:hidden"
                          size={20}
                        />
                      </Link>
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono tabular-nums text-xs ${mode === "videos" ? "text-ink" : "text-ink/60"}`}>
                      {formatCompact(c.videos)}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono tabular-nums text-xs ${mode === "views" ? "text-ink" : "text-ink/60"}`}>
                      {formatCompact(c.views)}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono tabular-nums text-xs ${mode === "channels" ? "text-ink" : "text-ink/60"}`}>
                      {formatCompact(c.channels)}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="h-1.5 bg-ink/8 relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-amber"
                          style={{ width: `${(c[mode] / max) * 100}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
